/**
 * The DICOM tag editor's form model (docs/reference/dicom-tag-editing.md): one field per
 * editable value in the study, its starting value as the files hold it, the checks a value
 * must pass before it is sent, and the edits a filled-in form amounts to.
 *
 * The checks mirror the server's (Shared.Services.Dicom.DicomTagCatalog) so a user sees a
 * problem as they type rather than on Save; the server still decides.
 */
import { parseSignalment } from '@/lib/patientSex';
const PATIENT_POSITION_TAG = '00185100';
const VIEW_POSITION_TAG = '00185101';
const IMAGE_LATERALITY_TAG = '00200062';
const BODY_PART_TAG = '00180015';
/** Cross-sectional modalities: Patient Position is theirs to state. */
const CROSS_SECTIONAL = new Set(['CT', 'MR', 'PT', 'NM']);
/** Projection radiography: View Position is theirs to state. */
const PROJECTION = new Set(['DX', 'CR', 'MG', 'RF', 'XA', 'RG', 'PX', 'IO']);
/** Common values offered for the code-string tags users type: free text, but a code. */
export const CODE_SUGGESTIONS = {
    [VIEW_POSITION_TAG]: [
        'VD',
        'DV',
        'LL',
        'RL',
        'AP',
        'PA',
        'LAT',
        'OBL',
        'CRCD',
        'DP',
        'PD',
        'ML',
        'LM',
    ],
    [BODY_PART_TAG]: [
        'THORAX',
        'ABDOMEN',
        'PELVIS',
        'SKULL',
        'HEAD',
        'NECK',
        'CSPINE',
        'TSPINE',
        'LSPINE',
        'SPINE',
        'EXTREMITY',
        'SHOULDER',
        'ELBOW',
        'CARPUS',
        'HIP',
        'STIFLE',
        'TARSUS',
        'PAW',
        'WHOLEBODY',
    ],
};
/** "00100010" as the standard writes it: "(0010,0010)". */
export function formatTag(tag) {
    return `(${tag.slice(0, 4)},${tag.slice(4, 8)})`;
}
const NO_MATCHED = new Set();
function mostCommon(values) {
    return values[0]?.value ?? '';
}
/** Every field of the editor, in the order it shows them: patient, study, then each series and its images. */
export function buildFields(data) {
    const fields = [];
    for (const def of data.tags) {
        if (def.level !== 'patient' && def.level !== 'study')
            continue;
        fields.push({
            key: `study:${def.tag}`,
            def,
            target: { kind: 'study' },
            initial: mostCommon(def.values),
            varies: def.values.length > 1 ? def.values : null,
            readable: def.values.length > 0,
        });
    }
    // Positioning tags are offered where they belong (Patient Position on cross-sectional studies,
    // View Position on radiographs, Image Laterality anywhere but a cross-sectional study) and
    // wherever a file already holds one.
    const modality = (data.modality ?? '').trim().toUpperCase();
    const seriesDefs = data.tags.filter((t) => t.level === 'series');
    const imageDefs = data.tags.filter((t) => t.level === 'image');
    for (const series of data.series) {
        for (const def of seriesDefs) {
            const values = series.values[def.tag] ?? [];
            const offered = def.tag !== PATIENT_POSITION_TAG ||
                CROSS_SECTIONAL.has(modality) ||
                values.some((v) => v.value !== '');
            if (!offered)
                continue;
            fields.push({
                key: `series:${series.seriesId}:${def.tag}`,
                def,
                target: { kind: 'series', series },
                initial: mostCommon(values),
                varies: values.length > 1 ? values : null,
                readable: values.length > 0,
            });
        }
        for (const image of series.images) {
            for (const def of imageDefs) {
                const value = image.values[def.tag] ?? null;
                const offered = (value ?? '') !== '' ||
                    (def.tag === VIEW_POSITION_TAG
                        ? PROJECTION.has(modality)
                        : def.tag !== IMAGE_LATERALITY_TAG || !CROSS_SECTIONAL.has(modality));
                if (!offered)
                    continue;
                fields.push({
                    key: `image:${image.imageId}:${def.tag}`,
                    def,
                    target: { kind: 'image', series, image },
                    initial: value ?? '',
                    varies: null,
                    readable: image.readable,
                });
            }
        }
    }
    return fields;
}
export function initialValues(fields) {
    return Object.fromEntries(fields.map((f) => [f.key, f.initial]));
}
/** What a value will compare as once sent: trimmed, defined terms upper-cased, one line-break style. */
export function normalizeValue(def, value) {
    if (def.vr === 'LT')
        return value.replace(/\r\n?/g, '\n').trim();
    const trimmed = value.trim();
    return def.vr === 'CS' ? trimmed.toUpperCase() : trimmed;
}
export function isChanged(field, values, matched = NO_MATCHED) {
    if (field.varies && matched.has(field.key))
        return true;
    return (normalizeValue(field.def, values[field.key] ?? '') !== normalizeValue(field.def, field.initial));
}
/** Latin-1 is what a file with no declared character set can be given, so it's what every file can take. */
const outsideLatin1 = (value) => [...value].some((c) => c.charCodeAt(0) > 0xff);
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;
// Long text keeps tabs (sent as spaces) and line breaks; nothing else below space.
// eslint-disable-next-line no-control-regex
const LONG_TEXT_CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Why a value can't go into its tag, or null when it can. `today` is yyyy-MM-dd. */
export function validateValue(def, raw, today) {
    const value = normalizeValue(def, raw);
    if (value === '')
        return def.required ? { code: 'required' } : null;
    if (def.vr === 'DA') {
        if (!ISO_DATE.test(value) || Number.isNaN(Date.parse(value)))
            return { code: 'date' };
        return value > today ? { code: 'future' } : null;
    }
    if (def.definedTerms) {
        return def.definedTerms.includes(value)
            ? null
            : { code: 'definedTerms', terms: def.definedTerms.join(', ') };
    }
    if (def.vr === 'CS') {
        // A code with no defined terms (View Position, Body Part Examined): upper-case letters,
        // digits, space and underscore.
        if (!/^[A-Z0-9 _]*$/.test(value))
            return { code: 'codeString' };
        return value.length > def.maxLength ? { code: 'tooLong', max: def.maxLength } : null;
    }
    if (outsideLatin1(value))
        return { code: 'charset' };
    if (def.vr === 'LT') {
        if (LONG_TEXT_CONTROL.test(value))
            return { code: 'control' };
        if (value.replace(/\n/g, '\r\n').length > def.maxLength)
            return { code: 'tooLong', max: def.maxLength };
        return null;
    }
    if (CONTROL.test(value))
        return { code: 'control' };
    if (value.includes('\\'))
        return { code: 'backslash' };
    if (value.length > def.maxLength)
        return { code: 'tooLong', max: def.maxLength };
    return null;
}
/** The fields whose value differs from the files, or that will bring them into line, in form order. */
export function changedFields(fields, values, matched = NO_MATCHED) {
    return fields.filter((f) => isChanged(f, values, matched));
}
/** The request body's edits for the changed fields. */
export function toEdits(changed, values) {
    return changed.map((f) => {
        const edit = { tag: f.def.tag, value: normalizeValue(f.def, values[f.key] ?? '') };
        if (f.target.kind === 'series')
            edit.seriesId = f.target.series.seriesId;
        if (f.target.kind === 'image')
            edit.imageId = f.target.image.imageId;
        return edit;
    });
}
/**
 * How many files the edits will rewrite: every file when any edit is study-wide, otherwise the
 * edited series plus the edited images outside them.
 */
export function filesTouched(data, changed) {
    if (changed.some((f) => f.target.kind === 'study'))
        return data.imageCount;
    const series = new Set();
    let count = 0;
    for (const f of changed) {
        if (f.target.kind === 'series' && !series.has(f.target.series.seriesId)) {
            series.add(f.target.series.seriesId);
            count += f.target.series.imageCount;
        }
    }
    const images = new Set();
    for (const f of changed) {
        if (f.target.kind === 'image' && !series.has(f.target.series.seriesId)) {
            images.add(f.target.image.imageId);
        }
    }
    return count + images.size;
}
/**
 * When every change only brings files into line on a value some of them already hold ("Make every
 * file match"), how many files it rewrites: the ones holding anything else. Exact for one tag; for
 * several, between the most any one tag has out of line and their sum, as the same odd file is
 * usually out of line on all of them. Null when any change sets a new value: then every file in
 * reach is rewritten.
 */
export function filesOutOfLine(changed, values, touched) {
    if (changed.length === 0)
        return null;
    let min = 0;
    let sum = 0;
    for (const f of changed) {
        if (!f.varies || isChanged(f, values))
            return null;
        const value = normalizeValue(f.def, values[f.key] ?? '');
        const odd = f.varies
            .filter((v) => normalizeValue(f.def, v.value) !== value)
            .reduce((n, v) => n + v.files, 0);
        min = Math.max(min, odd);
        sum += odd;
    }
    return { min: Math.min(min, touched), max: Math.min(sum, touched) };
}
/** Study-wide fields whose current value disagrees with the worklist's copy. */
export function worklistMismatches(fields, values) {
    // A field nothing could be read for has no file value to disagree with: "Use" would be a blind write.
    return fields.filter((f) => f.target.kind === 'study' &&
        f.readable &&
        f.def.inWorklist &&
        f.def.worklistValue !== null &&
        f.def.worklistValue.trim() !== '' &&
        normalizeValue(f.def, f.def.worklistValue) !== normalizeValue(f.def, values[f.key] ?? ''));
}
const SEX_FIELD = 'study:00100040';
const NEUTERED_FIELD = 'study:00102203';
/** A value a defined-terms tag holds that isn't one of its terms ("Male Castrated" in Patient's Sex). */
export function isNonStandardTerm(field, value) {
    const normalized = normalizeValue(field.def, value);
    return (!!field.def.definedTerms && normalized !== '' && !field.def.definedTerms.includes(normalized));
}
/**
 * When Patient's Sex or Patient's Sex Neutered holds free text instead of a defined term (vet
 * modalities and PIMS write "Male Castrated", "FS", "Spayed"), the conformant pair it means:
 * the same reading the worklist uses (lib/patientSex). Null when both hold defined terms or the
 * text says nothing recognisable.
 */
export function signalmentSuggestion(fields, values) {
    const sexField = fields.find((f) => f.key === SEX_FIELD);
    const neuteredField = fields.find((f) => f.key === NEUTERED_FIELD);
    if (!sexField || !neuteredField || !sexField.readable)
        return null;
    const sex = values[SEX_FIELD] ?? '';
    const neutered = values[NEUTERED_FIELD] ?? '';
    if (!isNonStandardTerm(sexField, sex) && !isNonStandardTerm(neuteredField, neutered))
        return null;
    const parsed = parseSignalment(sex, neutered);
    if (!parsed.sex && !parsed.neutered)
        return null;
    return { sex: parsed.sex, neutered: parsed.neutered };
}
export const SIGNALMENT_FIELDS = { sex: SEX_FIELD, neutered: NEUTERED_FIELD };
/** True when not one file of the study could be read: there is nothing to show or to edit. */
export function nothingReadable(fields) {
    return fields.length > 0 && fields.every((f) => !f.readable);
}
/** Today's date in the user's zone as yyyy-MM-dd, the form a date field speaks. */
export function localToday(now = new Date()) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
